import React, { useState, useEffect, useRef } from 'react';
import { Heart, MessageCircle, ArrowDown, Eye, ArrowUp, TrendingUp, Flame, Award, Users, Clock, ChevronUp, ChevronDown, Share2, Bookmark, Filter, Search, Sparkles, Zap, Star, Trophy, Gift, Bell, Plus, X, Reply, MoreHorizontal, MessageSquare, UserCircle } from 'lucide-react';
import { UserLevelCard } from '../components/UserLevelCard';
import ThreadDetailView from './ThreadDetailsPage';
import axios from 'axios';
import API_URL from '../config/ApiUrl';
import { useUser } from '../context/UserContext';
import { EnhancedCreateThreadModal } from '../components/ThreadPoll';
import { CommunityStatsCard } from '../components/CommunityStats';
import { TrendingCard } from '../components/TrendingCard';
import { PollComponent } from '../components/Poll';
import { AchievementsCard } from '../components/AchievementsCard';
import { useTheme } from '../arrangement/ThemeContext';
import { extractHashtags, formatContentWithHashtags } from '../utils/hashtagUtils';
import PublicProfileView from '../profile_component/PublicProfileViewPage';
import AuthGate from '../auth/AuthGate';

const MobileStatsToggle = ({ isMobile, colors }) => {
  const [activeTab, setActiveTab] = useState('trending');

  return (
    <div style={{
      background: colors.bgTertiary,
      borderRadius: '16px',
      border: '1px solid #333',
      overflow: 'hidden'
    }}>
      {/* Toggle Header */}
      <div style={{
        display: 'flex',
        background: colors.bgCard,
        borderBottom: '1px solid #333'
      }}>
        <button
          onClick={() => setActiveTab('trending')}
          style={{
            flex: 1,
            padding: '12px 16px',
            background: activeTab === 'trending' 
              ? 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' 
              : 'transparent',
            color: activeTab === 'trending' ? 'white' : '#888',
            border: 'none',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          📈 Trending
        </button>
        <button
          onClick={() => setActiveTab('stats')}
          style={{
            flex: 1,
            padding: '12px 16px',
            background: activeTab === 'stats' 
              ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' 
              : 'transparent',
            color: activeTab === 'stats' ? 'white' : '#888',
            border: 'none',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          👥 Community
        </button>
      </div>

      {/* Content Area - Just renders your existing components */}
      <div style={{ 
        padding: '0', // Remove padding since your components have their own
        background: 'transparent'
      }}>
        {activeTab === 'trending' ? (
          <TrendingCard isMobile={isMobile} />
        ) : (
          <CommunityStatsCard />
        )}
      </div>
    </div>
  );
};

const CreateThreadModal = ({ isOpen, onClose, onCreateThread, userSeshRooms, colors  }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [seshRoomId, setSeshRoomId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedSeshRoom, setSelectedSeshRoom] = useState(null);
  const [titleHashtags, setTitleHashtags] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [descriptionHashtags, setDescriptionHashtags] = useState([]);
  const [showSeshRooms, setShowSeshRooms] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setTitleHashtags(extractHashtags(title));
  }, [title]);

  useEffect(() => {
    setDescriptionHashtags(extractHashtags(description));
  }, [description]);

  const allHashtags = [...new Set([...titleHashtags, ...descriptionHashtags])];

  const handleSeshRoomSelection = (room) => {
    if (room === null) {
      setSelectedSeshRoom(null);
      setSeshRoomId(null);
    } else {
      setSelectedSeshRoom(room);
      setSeshRoomId(room.id);
    }
    setShowSeshRooms(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;

    setIsSubmitting(true);
    try {
      await onCreateThread({
        sesh_room_id: seshRoomId,
        title: title.trim(),
        description: description.trim(),
        isSeshRoom: seshRoomId !== null
      });
      setTitle('');
      setDescription('');
      setSelectedSeshRoom(null);
      setSeshRoomId(null);
      onClose();
    } catch (error) {
      console.error('Failed to create thread:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: isMobile ? '16px' : '0'
    }}>
      <div style={{
        background: colors.bgTertiary,
        borderRadius: '20px',
        padding: isMobile ? '20px' : '32px',
        width: isMobile ? '100%' : '90%',
        maxWidth: '600px',
        border: '1px solid #333',
        maxHeight: isMobile ? '90vh' : 'auto',
        overflowY: isMobile ? 'auto' : 'visible'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '24px',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '12px' : '0'
        }}>
          <h2 style={{ 
            margin: 0, 
            color: '#e5e5e5', 
            fontSize: isMobile ? '20px' : '24px', 
            fontWeight: '600',
            textAlign: isMobile ? 'center' : 'left'
          }}>
            ✨ Create New Thread
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#888',
              cursor: 'pointer',
              padding: '8px',
              position: isMobile ? 'absolute' : 'static',
              top: isMobile ? '16px' : 'auto',
              right: isMobile ? '16px' : 'auto'
            }}
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Rest of the form content remains the same but with responsive adjustments */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: '#e5e5e5', fontWeight: '500' }}>
              Post Location
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div
                onClick={() => handleSeshRoomSelection(null)}
                style={{
                  padding: isMobile ? '12px' : '16px',
                  border: '2px solid',
                  borderColor: selectedSeshRoom === null ? '#00d4aa' : '#333',
                  borderRadius: '12px',
                  background: selectedSeshRoom === null ? '#00d4aa20' : '#2a2a2a',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}
              >
                <div style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  border: '2px solid',
                  borderColor: selectedSeshRoom === null ? '#00d4aa' : '#666',
                  background: selectedSeshRoom === null ? '#00d4aa' : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {selectedSeshRoom === null && (
                    <div style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: 'white'
                    }} />
                  )}
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <UserCircle size={16} style={{ color: '#10b981' }} />
                    <span style={{ color: '#e5e5e5', fontWeight: '500', fontSize: isMobile ? '14px' : '16px' }}>Personal Thread</span>
                  </div>
                  <p style={{ margin: 0, fontSize: '12px', color: '#888' }}>
                    Post to your personal profile
                  </p>
                </div>
              </div>

              {userSeshRooms && userSeshRooms.length > 0 && (
                <>
                  <button
                    type="button"
                    onClick={() => setShowSeshRooms(!showSeshRooms)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      margin: '8px 0',
                      background: colors.bgCard,
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: '#888',
                      fontSize: isMobile ? '13px' : '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <span>
                      {selectedSeshRoom 
                        ? `Selected: ${selectedSeshRoom.name}` 
                        : `Or post to a Sesh Room (${userSeshRooms.length})`
                      }
                    </span>
                    <ChevronDown size={16} style={{ transform: showSeshRooms ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease' }} />
                  </button>
                  
                  {showSeshRooms && (
                    <div style={{
                      maxHeight: '200px',
                      overflowY: 'auto',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      background: '#1f1f1f'
                    }}>
                      {userSeshRooms.map((room) => (
                        <div
                          key={room.id}
                          onClick={() => handleSeshRoomSelection(room)}
                          style={{
                            padding: '12px 16px',
                            borderBottom: `1px solid ${colors.border}`,
                            cursor: 'pointer',
                            transition: 'background 0.3s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#333'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                          <Users size={16} style={{ color: '#3b82f6' }} />
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ color: '#e5e5e5', fontWeight: '500', fontSize: isMobile ? '14px' : '16px' }}>{room.name}</span>
                              {room.type === 'private' && (
                                <span style={{
                                  fontSize: '10px',
                                  background: '#ef4444',
                                  color: 'white',
                                  padding: '2px 6px',
                                  borderRadius: '4px'
                                }}>
                                  Private
                                </span>
                              )}
                            </div>
                            <p style={{ margin: 0, fontSize: '12px', color: '#888' }}>
                              {room.member_count || 0} members
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: '#e5e5e5', fontWeight: '500', fontSize: isMobile ? '14px' : '16px' }}>
              Thread Title * 
              <span style={{ color: '#888', fontSize: '12px', marginLeft: '8px' }}>
                Use #hashtags to categorize your thread
              </span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="What's on your mind? 🌿"
              style={{
                width: '100%',
                padding: isMobile ? '12px' : '16px',
                border: '2px solid #333',
                borderRadius: '12px',
                fontSize: isMobile ? '14px' : '16px',
                background: colors.bgCard,
                color: '#e5e5e5',
                outline: 'none',
                transition: 'border-color 0.3s ease',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#00d4aa'}
              onBlur={(e) => e.target.style.borderColor = '#333'}
            />
            {titleHashtags.length > 0 && (
              <div style={{ 
                marginTop: '8px', 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: '6px' 
              }}>
                {titleHashtags.map((tag, index) => (
                  <span
                    key={index}
                    style={{
                      padding: '4px 8px',
                      background: 'linear-gradient(135deg, #8b5cf620 0%, #7c3aed20 100%)',
                      border: '1px solid #8b5cf640',
                      borderRadius: '8px',
                      fontSize: '12px',
                      color: '#8b5cf6',
                      fontWeight: '500'
                    }}
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: '#e5e5e5', fontWeight: '500', fontSize: isMobile ? '14px' : '16px' }}>
              Description (Optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add more details about your thread... #hashtags work here too!"
              rows={isMobile ? 3 : 4}
              style={{
                width: '100%',
                padding: isMobile ? '12px' : '16px',
                border: '2px solid #333',
                borderRadius: '12px',
                fontSize: isMobile ? '14px' : '16px',
                background: colors.bgCard,
                color: '#e5e5e5',
                outline: 'none',
                resize: 'vertical',
                transition: 'border-color 0.3s ease',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#00d4aa'}
              onBlur={(e) => e.target.style.borderColor = '#333'}
            />
            
            {descriptionHashtags.length > 0 && (
              <div style={{ 
                marginTop: '8px', 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: '6px' 
              }}>
                {descriptionHashtags.map((tag, index) => (
                  <span
                    key={index}
                    style={{
                      padding: '4px 8px',
                      background: 'linear-gradient(135deg, #10b98120 0%, #059e6b20 100%)',
                      border: '1px solid #10b98140',
                      borderRadius: '8px',
                      fontSize: '12px',
                      color: '#10b981',
                      fontWeight: '500'
                    }}
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          {allHashtags.length > 0 && (
            <div style={{
              marginBottom: '24px',
              padding: isMobile ? '12px' : '16px',
              background: colors.bgCard,
              borderRadius: '12px',
              border: '1px solid #333'
            }}>
              <h4 style={{
                margin: '0 0 12px 0',
                fontSize: '14px',
                fontWeight: '600',
                color: '#e5e5e5',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                🏷️ Thread Tags ({allHashtags.length})
              </h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {allHashtags.map((tag, index) => (
                  <span
                    key={index}
                    style={{
                      padding: '6px 12px',
                      background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                      color: 'white',
                      borderRadius: '10px',
                      fontSize: '12px',
                      fontWeight: '600',
                      boxShadow: '0 2px 4px rgba(139, 92, 246, 0.3)'
                    }}
                  >
                    #{tag}
                  </span>
                ))}
              </div>
              <p style={{
                margin: '12px 0 0 0',
                fontSize: '12px',
                color: '#888',
                lineHeight: '1.4'
              }}>
                These hashtags will help others discover your thread and make it easier to find related discussions.
              </p>
            </div>
          )}

          <div style={{ 
            display: 'flex', 
            gap: '12px', 
            justifyContent: 'flex-end',
            flexDirection: isMobile ? 'column' : 'row'
          }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '12px 24px',
                border: '2px solid #333',
                borderRadius: '12px',
                background: 'transparent',
                color: '#888',
                cursor: 'pointer',
                fontSize: isMobile ? '14px' : '16px',
                fontWeight: '500',
                order: isMobile ? 2 : 1
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!title.trim() || isSubmitting}
              style={{
                padding: '12px 24px',
                border: 'none',
                borderRadius: '12px',
                background: title.trim() ? 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)' : '#333',
                color: 'white',
                cursor: title.trim() ? 'pointer' : 'not-allowed',
                fontSize: isMobile ? '14px' : '16px',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                order: isMobile ? 1 : 2
              }}
            >
              {isSubmitting ? 'Creating...' : 
                selectedSeshRoom ? `🚀 Post to ${selectedSeshRoom.name}` : '🚀 Create Personal Thread'
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ThreadCard = ({ thread, onVote, onOpenThread, onHashtagClick, getCurrentUserId, setSelectedUserId, setShowProfileView, colors }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (thread.type == 'poll'){
    console.log('IN THE POLLL HERE WE ARE',thread)
  }
  console.log(thread)

  const handleVote = async (value) => {
    try {
      await onVote(thread.id, value);
    } catch (error) {
      console.error('Failed to vote:', error);
    }
  };

  const handleUsernameClick = (userId) => {
    console.log('====================HANDLE GET CLICK ========================')
    console.log(userId)
    console.log(getCurrentUserId())
    console.log('====================HANDLE GET CLICK ========================')

    if (userId && userId !== getCurrentUserId()) {
      setSelectedUserId(userId);
      setShowProfileView(true);
    }
  };

  const getSmokerEmoji = (level) => {
    switch(level) {
      case 'beginner': return 'https://highmind-bucket.s3.us-east-1.amazonaws.com/cdb53b7a8c7e4624a810c0d654ca03c8_Smoker_beginer.webp';
      case 'casual': return 'https://highmind-bucket.s3.us-east-1.amazonaws.com/e72017f33d1c480db1928ca3d7295de4_Smoker_Level_cas.webp';
      case 'connoisseur': return 'https://highmind-bucket.s3.us-east-1.amazonaws.com/3e1e13711dd441fcb7c34c155416ee69_Smoker_Level_con.webp';
      case 'stoner': return 'https://highmind-bucket.s3.us-east-1.amazonaws.com/9084554e27d747188148530bc7899eee_Smoker_Level_Logo_Stoner.webp';
      default: return 'https://highmind-bucket.s3.us-east-1.amazonaws.com/6d4ba06007624c3691fd682dfbb31cdf_hmind.png'
    }
  };

  return (
    <div style={{
      background: colors.bgPrimary,
      borderRadius: '20px',

      width:  isMobile ? '90%' : '',

      padding: isMobile ? '16px' : '24px',
      border: '1px solid #333',
      transition: 'all 0.3s ease',
      cursor: 'pointer'
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.borderColor = '#00d4aa';
      e.currentTarget.style.transform = 'translateY(-2px)';
      e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 212, 170, 0.15)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.borderColor = '#333';
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = 'none';
    }}
    onClick={() => onOpenThread(thread)}
    > 
      <div style={{ 
        display: 'flex', 
        alignItems: 'flex-start', 
        gap: isMobile ? '12px' : '20px',
        flexDirection: isMobile ? 'column' : 'row'
      }}>
        
        {/* Voting Section - Mobile: Top, Desktop: Left */}
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'row' : 'column',
          alignItems: 'center',
          justifyContent: isMobile ? 'center' : 'flex-start',
          gap: isMobile ? '16px' : '8px',
          minWidth: isMobile ? 'auto' : '70px',
          width: isMobile ? '90%' : 'auto',
          background: colors.vote,
          padding: isMobile ? '12px 16px' : '16px 12px',
          borderRadius: '16px',
          border: '1px solid #333',
          order: isMobile ? 2 : 1
        }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleVote(thread.user_vote === 1 ? 0 : 1);
            }}
            style={{
              padding: isMobile ? '6px' : '8px',
              borderRadius: '12px',
              border: 'none',
              background: thread.user_vote === 1 
                ? 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)' 
                : 'white',
              color: thread.user_vote === 1 ? 'white' : '#6b7280',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: thread.user_vote === 1 ? 'scale(1.1)' : 'scale(1)',
              boxShadow: thread.user_vote === 1 ? '0 4px 12px rgba(16, 185, 129, 0.4)' : '0 2px 4px rgba(0, 0, 0, 0.1)'
            }}
            onMouseEnter={(e) => {
              if (thread.user_vote !== 1) {
                e.currentTarget.style.background = 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)';
                e.currentTarget.style.color = '#00d4aa';
                e.currentTarget.style.transform = 'scale(1.05)';
              }
            }}
            onMouseLeave={(e) => {
              if (thread.user_vote !== 1) {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.color = '#6b7280';
                e.currentTarget.style.transform = 'scale(1)';
              }
            }}
          >
            <ArrowUp size={isMobile ? 16 : 20} />
          </button>
          
          <span style={{
            fontWeight: 'bold',
            fontSize: isMobile ? '16px' : '20px',
            color: thread.user_vote === 1 ? '#00d4aa' : 
                   thread.user_vote === -1 ? '#ef4444' : '#374151',
            transition: 'all 0.3s ease'
          }}>
            {thread.likes}
          </span>
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleVote(thread.user_vote === -1 ? 0 : -1);
            }}
            style={{
              padding: isMobile ? '6px' : '8px',
              borderRadius: '12px',
              border: 'none',
              background: thread.user_vote === -1 
                ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' 
                : 'white',
              color: thread.user_vote === -1 ? 'white' : '#6b7280',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: thread.user_vote === -1 ? 'scale(1.1)' : 'scale(1)',
              boxShadow: thread.user_vote === -1 ? '0 4px 12px rgba(239, 68, 68, 0.4)' : '0 2px 4px rgba(0, 0, 0, 0.1)'
            }}
            onMouseEnter={(e) => {
              if (thread.user_vote !== -1) {
                e.currentTarget.style.background = 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)';
                e.currentTarget.style.color = '#ef4444';
                e.currentTarget.style.transform = 'scale(1.05)';
              }
            }}
            onMouseLeave={(e) => {
              if (thread.user_vote !== -1) {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.color = '#6b7280';
                e.currentTarget.style.transform = 'scale(1)';
              }
            }}
          >
            <ArrowDown size={isMobile ? 16 : 20} />
          </button>
        </div>

        {/* Content Section */}
        <div style={{ flex: 1, width: isMobile ? '100%' : '', order: isMobile ? 1 : 2 }}>
          {thread.sesh_room_info ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '16px',
              padding: isMobile ? '6px 10px' : '8px 12px',
              background: 'linear-gradient(135deg, #3b82f620 0%, #3b82f610 100%)',
              border: '1px solid #3b82f640',
              borderRadius: '12px'
            }}>
              <Users size={isMobile ? 12 : 14} style={{ color: '#3b82f6' }} />
              <span style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: '500', color: '#3b82f6' }}>
                Sesh Room Thread: {thread.sesh_room_info.name}
              </span>
            </div>
          ) : (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '16px',
              padding: isMobile ? '6px 10px' : '8px 12px',
              background: 'linear-gradient(135deg, #10b98120 0%, #10b98110 100%)',
              border: '1px solid #10b98140',
              borderRadius: '12px'
            }}>
              <UserCircle size={isMobile ? 12 : 14} style={{ color: '#10b981' }} />
              <span style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: '500', color: '#10b981' }}>
                Personal Thread
              </span>
            </div>
          )}

          {/* Author and avatar section */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'flex-start', 
            gap: isMobile ? '12px' : '16px', 
            marginBottom: '16px',
            flexDirection: isMobile ? 'column' : 'row'
          }}>
            {/* Avatar with smoker level badge */}
            <div style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              alignSelf: isMobile ? 'flex-start' : 'auto',
              gap: isMobile ? '12px' : '0'
            }}>    
              <img
                src={thread.avatar || thread.user_avatar}
                alt={thread.author}
                style={{
                  width: isMobile ? '40px' : '48px',
                  height: isMobile ? '40px' : '48px',
                  borderRadius: '12px',
                  objectFit: 'cover'
                }}
              />
                      
              {/* Smoker Level Badge */}
              <div style={{
                position: isMobile ? 'static' : 'absolute',
                bottom: isMobile ? 'auto' : '-170%',
                right: isMobile ? 'auto' : 'auto',
                backgroundColor: colors.imgBackground,
                borderRadius: '50%',
                width: isMobile ? '40px' : '48px',
                height: isMobile ? '40px' : '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                border: '1px solid #00d4aa'
              }}>
                <img 
                  src={getSmokerEmoji(thread.author_smoke_level)}
                  alt={`${thread.author_smoke_level} level`}
                  style={{
                    width: isMobile ? '36px' : '44px',
                    height: isMobile ? '36px' : '44px',
                    borderRadius: '50%',
                    objectFit: 'cover'
                  }}
                />
              </div>
            </div>

            {/* Thread content */}
            <div style={{ flex: 1, width: isMobile ? '100%' : 'auto' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: isMobile ? 'flex-start' : 'center', 
                gap: '12px', 
                marginBottom: '8px',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: isMobile ? 'flex-start' : 'center'
              }}>
                <h3 style={{
                  margin: 0,
                  fontSize: isMobile ? '16px' : '18px',
                  fontWeight: '600',
                  color: '#e5e5e5',
                  lineHeight: '1.3'
                }}>
                  {formatContentWithHashtags(thread.title, onHashtagClick)}
                </h3>
                {thread.trending && (
                  <div style={{
                    padding: '4px 8px',
                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    borderRadius: '8px',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    alignSelf: isMobile ? 'flex-start' : 'auto'
                  }}>
                    <Flame size={12} />
                    Trending
                  </div>
                )}
              </div>

              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px', 
                marginBottom: '12px',
                flexWrap: 'wrap'
              }}>
                <span 
                  className={thread.user_id !== getCurrentUserId() ? 'clickable-username' : ''}
                  style={{ 
                    fontSize: isMobile ? '13px' : '14px', 
                    color: '#888',
                    cursor: thread.user_id !== getCurrentUserId() ? 'pointer' : 'default',
                    position: 'relative'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUsernameClick(thread.author_user_id);
                  }}
                >
                  by {thread.author_handle || thread.author}
                </span>

                <span style={{ fontSize: isMobile ? '13px' : '14px', color: '#666' }}>•</span>
                <span style={{ fontSize: isMobile ? '13px' : '14px', color: '#888' }}>
                  {thread.created_at}
                </span>
              </div>

              {thread.description && (
                <p style={{
                  margin: '0 0 16px 0',
                  fontSize: isMobile ? '14px' : '15px',
                  color: colors.textPrimary1,
                  lineHeight: '1.5'
                }}>
                  {formatContentWithHashtags(thread.description, onHashtagClick)}
                </p> 
              )}
              
              {thread.type === "poll" && thread.pollOptions && (
                <div style={{ margin: '12px 0 16px 0' }}>
                  <PollComponent
                    options={thread.pollOptions}
                    totalVotes={thread.totalPollVotes}
                    pollEndsIn={thread.pollEndsIn}
                    userVoted={thread.poll?.has_voted}
                    onVote={async (optionId) => {
                      await axios.post(
                        `/api/thread/${thread.id}/poll/vote`,
                        { option_id: optionId },
                        { withCredentials: true }
                      );
                    }}
                    page = {'threads'}
                  />
                </div>
              )}

              {/* Bottom action bar */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? '12px' : '0',
                alignItems: isMobile ? 'flex-start' : 'center'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#888' }}>
                    <MessageCircle size={16} />
                    <span style={{ fontSize: isMobile ? '13px' : '14px' }}>{thread.messages || 0}</span>
                  </div>
                </div>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenThread(thread);
                  }}
                  style={{
                    padding: isMobile ? '8px 12px' : '8px 16px',
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontSize: isMobile ? '13px' : '14px',
                    fontWeight: '500',
                    transition: 'all 0.3s ease',
                    alignSelf: isMobile ? 'flex-end' : 'auto'
                  }}
                >
                  Join Discussion →
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export function ThreadsPage() {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeHashtag, setActiveHashtag] = useState(null);
  const [showProfileView, setShowProfileView] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [activeFilter, setActiveFilter] = useState('All');
  const [loadingMore, setLoadingMore] = useState(false);
  const [profileData, setProfileData] = useState({ achievements: [] });
  const [achievementsLoading, setAchievementsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('new');
  const [searchTerm, setSearchTerm] = useState('');
  const [createModalType, setCreateModalType] = useState('discussion');
  const [showCreateThread, setShowCreateThread] = useState(false);
  const [notifications, setNotifications] = useState(3);
  const [selectedThread, setSelectedThread] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [userSeshRooms, setUserSeshRooms] = useState([]);
  const [loadingSeshRooms, setLoadingSeshRooms] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [threadsPerPage, setThreadsPerPage] = useState(15);
  const [totalThreads, setTotalThreads] = useState(0);
  const [hasMoreThreads, setHasMoreThreads] = useState(true);
  const [allThreadsLoaded, setAllThreadsLoaded] = useState(false);
  const [showThreadDetail, setShowThreadDetail] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const { colors } = useTheme(); 

  const { user, isAuthenticated, logout, refreshUserStats } = useUser();

  // Add resize listener
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleHashtagClick = (tag) => {
    setActiveHashtag(tag);
    setSearchTerm(`#${tag}`);
  };

  const clearHashtagFilter = () => {
    setActiveHashtag(null);
    setSearchTerm('');
    setActiveFilter('All');
  };

  const clearFilterOnly = () => {
    setActiveFilter('All');
    setSearchTerm('');
    setActiveHashtag(null);
  };

  const handleFilterClick = (filter) => {
    setActiveFilter(filter);
    
    if (filter === 'All') {
      setSearchTerm('');
      setActiveHashtag(null);
    } else {
      setSearchTerm(`#${filter.toLowerCase()}`);
      setActiveHashtag(filter.toLowerCase());
    }
  };

  const getSmokerEmoji = (level) => {
    switch(level) {
      case 'beginner': return '🌱';
      case 'casual': return '🌿';
      case 'connoisseur': return '🍃';
      case 'stoner': return '🔥';
      default: return '👤';
    }
  };

  const fetchUserStats = async () => {
    try {
      setStatsLoading(true);
      const response = await fetch(`${API_URL}/api/user/stats`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      if (result.success) {
        setUserStats(result.data);
      } else {
        console.error('Failed to fetch user stats:', result.error);
        setUserStats({
          level: 1,
          xp: 0,
          nextLevelXp: 100,
          get_next_level_threshold: 100,
          streak: 0,
          badges: [],
          reputation: 0,
          posts: 0,
          comments: 0,
          upvotes: 0,
          threads: 0,
          reviews: 0,
          helpful_answers: 0,
          sesh_rooms_joined: 0
        });
      }
    } catch (error) {
      console.error('Error fetching user stats:', error);
      setUserStats({
        level: 1,
        xp: 0,
        nextLevelXp: 100,
        get_next_level_threshold: 100,
        streak: 0,
        badges: [],
        reputation: 0,
        posts: 0,
        comments: 0,
        upvotes: 0,
        threads: 0,
        reviews: 0,
        helpful_answers: 0,
        sesh_rooms_joined: 0
      });
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchAchievements = async () => {
    try {
      setAchievementsLoading(true);
      const response = await fetch(`${API_URL}/api/achievements`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      if (result.success) {
        setProfileData(result.data);
      } else {
        console.error('Failed to fetch achievements:', result.error);
        setProfileData({ achievements: [] });
      }
    } catch (error) {
      console.error('Error fetching achievements:', error);
      setProfileData({ achievements: [] });
    } finally {
      setAchievementsLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
    fetchThreads(1, threadsPerPage, true);
  }, [sortBy, searchTerm, threadsPerPage]);

  useEffect(() => {
    if (user?.id) {
      fetchUserSeshRooms();
      fetchUserStats();
      fetchAchievements();
    }
  }, [user]);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .clickable-username {
        position: relative;
        cursor: pointer;
        transition: color 0.3s ease;
      }
      
      .clickable-username::after {
        content: '';
        position: absolute;
        bottom: -2px;
        left: 0;
        width: 0;
        height: 1px;
        background: linear-gradient(135deg, #00d4aa 0%, #10b981 100%);
        transition: width 0.3s ease;
      }
      
      .clickable-username:hover {
        color: #00d4aa !important;
      }
      
      .clickable-username:hover::after {
        width: 100%;
      }
    `;
    document.head.appendChild(style);
    return () => {
      if (style.parentNode) {
        document.head.removeChild(style);
      }
    };
  }, []);

  const getCurrentUserId = () => {
    return user.id; 
  };

  const fetchUserSeshRooms = async () => {
    if (!user?.id) return;
    
    try {
      setLoadingSeshRooms(true);
      const response = await axios.get(`${API_URL}/api/user/${user.id}/sesh-rooms`, {
        withCredentials: true
      });
      setUserSeshRooms(response.data || []);
    } catch (error) {
      console.error('Failed to fetch user sesh rooms:', error);
      setUserSeshRooms([]);
    } finally {
      setLoadingSeshRooms(false);
    }
  };
  
  const handleLoadMore = () => {
    if (!loadingMore && hasMoreThreads) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchThreads(nextPage, threadsPerPage);
    }
  };

  const handleThreadsPerPageChange = (newLimit) => {
    setThreadsPerPage(newLimit);
    setCurrentPage(1);
  };

  const fetchThreads = async (page = 1, limit = threadsPerPage, reset = false) => {
    try {
      if (page === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const response = await axios.get(`${API_URL}/api/get-threads`, {
        params: {
          page,
          limit,
          sortBy,
          search: searchTerm,
          user_id: user.id,
        },
        withCredentials: true
      });

      

      const { threads: newThreads, totalCount, hasMore } = response.data;

      if (reset || page === 1) {
        setThreads(newThreads);
      } else {
        setThreads(prev => [...prev, ...newThreads]);
      }

      setTotalThreads(totalCount);
      setHasMoreThreads(hasMore);
      setAllThreadsLoaded(!hasMore);

    } catch (error) {
      console.error('Failed to fetch threads:', error);
      setError('Failed to load threads');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleCreateThread = async (threadData) => {
    try {
      const response = await axios.post(`${API_URL}/api/create-thread`, threadData, {
        withCredentials: true
      });
      if (response.statusText == "CREATED") {
        console.log('Thread created successfully!');
        refreshUserStats(false);
      }
      setThreads(prev => [response.data, ...prev]);
    } catch (error) {
      console.error('Failed to create thread:', error);
      throw error;
    }
  };

  const handleVoteThread = async (threadId, value) => {
    try {
      const response = await axios.post(`${API_URL}/api/thread/vote`, {
        thread_id: threadId,
        user_id: getCurrentUserId(),
        value: value
      }, {
        withCredentials: true
      });

      setThreads(prev => prev.map(thread => 
        thread.id === threadId 
          ? { 
              ...thread, 
              user_vote: value === 1,
              likes: response.data.total_likes
            }
          : thread
      ));
    } catch (error) {
      console.error('Failed to vote on thread:', error);
      throw error;
    }
  };

  const handleOpenThread = (thread) => {
    console.log('Opening thread:', thread);
    setSelectedThread(thread);
    setShowThreadDetail(true);
  };

  if (!isAuthenticated) {
    return (
      <AuthGate 
        pageName="Discover" 
        pageIcon={<Heart size={64} />}
        customMessage="Discover your perfect cannabis products with our smart recommendation engine"
        features={[
          { icon: <Heart size={20} />, text: "Swipe and rate thousands of products", color: "#ef4444" },
          { icon: <Star size={20} />, text: "Get personalized recommendations", color: "#fbbf24" },
          { icon: <TrendingUp size={20} />, text: "Build your taste profile", color: "#00d4aa" },
          { icon: <Award size={20} />, text: "Unlock achievements and rewards", color: "#8b5cf6" }
        ]}
      />
    );
  }

  const filteredThreads = threads
    .filter(thread => 
      thread.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (thread.description && thread.description.toLowerCase().includes(searchTerm.toLowerCase()))
    )
    .sort((a, b) => {
      switch (sortBy) {
        case 'hot': 
          return (b.likes + (b.messages || 0) * 2 + (b.trending ? 10 : 0)) - 
                 (a.likes + (a.messages || 0) * 2 + (a.trending ? 10 : 0));
        case 'new': 
          return new Date(b.created_at) - new Date(a.created_at);
        case 'top': 
          return b.likes - a.likes;
        case 'trending':
          return (b.trending ? 1 : 0) - (a.trending ? 1 : 0);
        default: 
          return 0;
      }
    });

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#0a0a0a',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            fontSize: '48px',
            marginBottom: '16px',
            animation: 'spin 1s linear infinite'
          }}>🌿</div>
          <p style={{ color: '#888' }}>Loading threads...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#0a0a0a',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
          <p style={{ color: '#ef4444' }}>{error}</p>
          <button
            onClick={() => fetchThreads(1, threadsPerPage, true)}
            style={{
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: '600',
              marginTop: '16px'
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#0a0a0a',
      color: 'white',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      
    }}>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.8; }
        }
        
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .slide-in {
          animation: slideIn 0.5s ease-out;
        }
      `}</style>

      <div style={{ 
        maxWidth: '1400px', 
        margin: '0 auto', 
        padding: isMobile ? '16px' : '24px' 
      }}>
        {/* Enhanced Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: isMobile ? '24px' : '32px',
          background: colors.comeCard,
          padding: isMobile ? '16px 20px' : '24px 32px',
          
          borderRadius: '20px',
          border: '1px solid #333',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '16px' : '0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', textAlign: isMobile ? 'center' : 'left' }}>
            <div style={{
              width: isMobile ? '50px' : '60px',
              height: isMobile ? '50px' : '60px',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              borderRadius: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 20px rgba(139, 92, 246, 0.3)'
            }}>
              <MessageSquare size={isMobile ? 28 : 32} style={{ color: 'white' }} />
            </div>
            <div>
              <h1 style={{
                margin: 0,
                fontSize: isMobile ? '24px' : '32px',
                fontWeight: '800',
                background: colors.header,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                lineHeight: '1.2'
              }}>
                Discussion Threads
              </h1>
              <p style={{
                margin: 0,
                color: '#888',
                fontSize: isMobile ? '14px' : '16px',
                fontWeight: '500'
              }}>
                Deep conversations and threaded discussions 💬
              </p>
            </div>
          </div>
          
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: isMobile ? '8px' : '16px',
            flexDirection: isMobile ? 'row' : 'row',
            width: isMobile ? '100%' : 'auto',
            justifyContent: isMobile ? 'space-between' : 'flex-end'
          }}>
            <button
              onClick={() => { setCreateModalType('discussion'); setShowCreateThread(true); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: isMobile ? '8px' : '12px',
                padding: isMobile ? '10px 14px' : '16px 24px',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '16px',
                cursor: 'pointer',
                fontSize: isMobile ? '13px' : '16px',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(139, 92, 246, 0.4)',
                flex: isMobile ? 1 : 'auto',
                minWidth: 0, // Allow shrinking
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 20px rgba(139, 92, 246, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(139, 92, 246, 0.4)';
              }}
            >
              <MessageSquare size={isMobile ? 14 : 20} />
              {isMobile ? 'Thread' : 'Start Thread'}
            </button>
            <button 
              onClick={() => { setCreateModalType('poll'); setShowCreateThread(true); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: isMobile ? '8px' : '12px',
                padding: isMobile ? '10px 14px' : '16px 24px',
                background: 'white',
                color: '#888',
                border: '2px solid #e5e7eb',
                borderRadius: '16px',
                cursor: 'pointer',
                fontSize: isMobile ? '13px' : '16px',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                flex: isMobile ? 1 : 'auto',
                minWidth: 0, // Allow shrinking
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#8b5cf6';
                e.currentTarget.style.color = '#8b5cf6';
                e.currentTarget.style.background = 'linear-gradient(135deg, #fef7ff 0%, #f3e8ff 100%)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.color = '#6b7280';
                e.currentTarget.style.background = 'white';
              }}
            >
              📊 Poll
            </button>
          </div>
        </div>

        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: isMobile ? '1fr' : '1fr 3fr 1fr', 
          gap: isMobile ? '24px' : '32px' 
        }}>
          {/* Left Sidebar - Hidden on mobile or stacked */}
          {!isMobile && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <UserLevelCard stats={userStats} isAuthenticated={isAuthenticated} />
              <AchievementsCard 
                profileData={profileData} 
                loading={achievementsLoading} 
                isAuthenticated={isAuthenticated} 
              />
            </div>
          )}

          {/* Main Content */}
          <div style={{ order: isMobile ? 1 : 0 }}>
            {/* Enhanced Search and Filters */}
            <div style={{
              background: colors.bgCard,
              borderRadius: '20px',
              padding: isMobile ? '16px' : '24px',
              border: '2px solid #333',
              color: '#e5e5e5',
              marginBottom: '24px',
              width: isMobile ? '90%' : '',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '16px', 
                marginBottom: '16px',
                flexDirection: isMobile ? 'column' : 'row'
              }}>
                <div style={{ position: 'relative', flex: 1, width: isMobile ? '100%' : 'auto' }}>
                  <Search size={20} style={{
                    position: 'absolute',
                    left: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#888'
                  }} />
                  <input
                    type="text"
                    placeholder="Search threads and discussions... 🔍"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      width: '100%',
                      paddingLeft: '48px',
                      paddingRight: '16px',
                      paddingTop: isMobile ? '12px' : '16px',
                      paddingBottom: isMobile ? '12px' : '16px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '16px',
                      fontSize: isMobile ? '14px' : '16px',
                      outline: 'none',
                      transition: 'all 0.3s ease',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#8b5cf6';
                      e.currentTarget.style.boxShadow = '0 0 0 4px rgba(139, 92, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  />
                </div>
                
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  width: isMobile ? '100%' : 'auto'
                }}>
                  <Filter size={16} style={{ color: '#888' }} />
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    style={{
                      padding: isMobile ? '12px 16px' : '16px 20px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '16px',
                      fontSize: '14px',
                      fontWeight: '500',
                      outline: 'none',
                      cursor: 'pointer',
                      background: 'white',
                      width: isMobile ? '100%' : 'auto'
                    }}
                  >
                    <option value="hot">🔥 Hot</option>
                    <option value="new">⚡ New</option>
                    <option value="top">🏆 Top</option>
                    <option value="trending">📈 Trending</option>
                  </select>
                </div>
              </div>

              {/* Threads per page selector and stats */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '16px',
                paddingTop: '16px',
                borderTop: '1px solid #444',
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? '12px' : '0'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px',
                  width: isMobile ? '100%' : 'auto',
                  justifyContent: isMobile ? 'space-between' : 'flex-start'
                }}>
                  <span style={{ fontSize: '14px', color: '#888' }}>Show per page:</span>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {[15, 25, 50].map((count) => (
                      <button
                        key={count}
                        onClick={() => handleThreadsPerPageChange(count)}
                        style={{
                          padding: isMobile ? '6px 10px' : '8px 12px',
                          background: threadsPerPage === count 
                            ? 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' 
                            : 'white',
                          color: threadsPerPage === count ? 'white' : '#6b7280',
                          border: '1px solid',
                          borderColor: threadsPerPage === count ? '#8b5cf6' : '#e5e7eb',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: '500',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        {count}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div style={{ 
                  fontSize: '14px', 
                  color: '#888',
                  textAlign: isMobile ? 'center' : 'right'
                }}>
                  Showing {threads.length} of {totalThreads} threads
                </div>
              </div>

              {/* Quick Topic Filters */}
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: '8px',
                justifyContent: isMobile ? 'center' : 'flex-start'
              }}>
                {['All', 'General', 'Medical', 'Growing', 'Reviews', 'Q&A', 'Off-Topic'].map((filter, index) => (
                  <button
                    key={filter}
                    onClick={() => handleFilterClick(filter)}
                    style={{
                      padding: isMobile ? '6px 12px' : '8px 16px',
                      background: activeFilter === filter 
                        ? 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' 
                        : 'white',
                      color: activeFilter === filter ? 'white' : '#6b7280',
                      border: '1px solid',
                      borderColor: activeFilter === filter ? '#8b5cf6' : '#e5e7eb',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '500',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (activeFilter !== filter) {
                        e.currentTarget.style.background = 'linear-gradient(135deg, #fef7ff 0%, #f3e8ff 100%)';
                        e.currentTarget.style.borderColor = '#8b5cf6';
                        e.currentTarget.style.color = '#7c3aed';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (activeFilter !== filter) {
                        e.currentTarget.style.background = 'white';
                        e.currentTarget.style.borderColor = '#e5e7eb';
                        e.currentTarget.style.color = '#6b7280';
                      }
                    }}
                  >
                    {filter}
                  </button>
                ))}
              </div>
            </div>

            {/* Active filter indicator */}
            {(activeHashtag || (activeFilter !== 'All')) && (
              <div style={{
                margin: '0 0 24px 0',
                padding: '12px 16px',
                background: 'linear-gradient(135deg, #8b5cf620 0%, #7c3aed20 100%)',
                border: '1px solid #8b5cf640',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? '8px' : '0'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '14px', color: '#e5e5e5' }}>
                    {activeHashtag && activeFilter === 'All' 
                      ? 'Filtering by hashtag:' 
                      : activeFilter !== 'All' 
                      ? 'Filtering by category:' 
                      : 'Active filter:'
                    }
                  </span>
                  <span style={{
                    padding: '4px 8px',
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    color: 'white',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    {activeHashtag && activeFilter === 'All' 
                      ? `#${activeHashtag}` 
                      : activeFilter !== 'All' 
                      ? `#${activeFilter.toLowerCase()}` 
                      : ''
                    }
                  </span>
                </div>
                <button
                  onClick={() => {
                    if (activeFilter !== 'All') {
                      clearFilterOnly();
                    } else {
                      clearHashtagFilter();
                    }
                  }}
                  style={{
                    padding: '4px 8px',
                    background: 'transparent',
                    border: '1px solid #8b5cf6',
                    borderRadius: '6px',
                    color: '#8b5cf6',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  Clear
                </button>
              </div>
            )}

            {/* Mobile Stats Cards - Show above threads on mobile */}
           {isMobile && (
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '16px', 
                marginBottom: '24px' 
              }}>
                <UserLevelCard stats={userStats} isAuthenticated={isAuthenticated} />
                <MobileStatsToggle isMobile={isMobile} colors={colors}/>
              </div>
            )}

            {/* Threads Feed */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '16px' : '24px' }}>
              {threads.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: isMobile ? '32px 16px' : '48px 24px',
                  background: colors.bgTertiary,
                  borderRadius: '20px',
                  border: '1px solid #333'
                }}>
                  <div style={{ fontSize: isMobile ? '40px' : '48px', marginBottom: '16px' }}>💬</div>
                  <h3 style={{ 
                    margin: '0 0 8px 0', 
                    color: '#e5e5e5', 
                    fontSize: isMobile ? '18px' : '20px', 
                    fontWeight: '600' 
                  }}>
                    {searchTerm ? 'No threads found' : 'No threads yet'}
                  </h3>
                  <p style={{ 
                    margin: '0 0 24px 0', 
                    color: '#888', 
                    fontSize: isMobile ? '14px' : '16px' 
                  }}>
                    {searchTerm ? 'Try adjusting your search terms' : 'Be the first to start a discussion!'}
                  </p>
                  {!searchTerm && (
                    <button
                      onClick={() => setShowCreateThread(true)}
                      style={{
                        padding: '12px 24px',
                        background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        fontSize: isMobile ? '14px' : '16px',
                        fontWeight: '600'
                      }}
                    >
                      Start First Thread
                    </button>
                  )}
                </div>
              ) : (
                threads.map((thread, index) => (
                  <div key={thread.id} className="slide-in" style={{
                    animationDelay: `${index * 0.1}s`
                  }}>
                    <ThreadCard
                      thread={thread}
                      onVote={handleVoteThread}
                      onOpenThread={handleOpenThread}
                      onHashtagClick={handleHashtagClick}
                      getCurrentUserId={getCurrentUserId}
                      setSelectedUserId={setSelectedUserId}
                      setShowProfileView={setShowProfileView}
                      colors={colors}
                    />
                  </div>
                ))
              )}
            </div>

            {/* Enhanced Load More Section */}
            {threads.length > 0 && (
              <div style={{
                marginTop: '32px',
                textAlign: 'center',
                padding: isMobile ? '24px 16px' : '32px',
                background: colors.bgTertiary,
                borderRadius: '20px',
                border: '1px solid #333'
              }}>
                {hasMoreThreads ? (
                  <div style={{ marginBottom: '16px' }}>
                    <span style={{ fontSize: isMobile ? '28px' : '32px', marginBottom: '8px', display: 'block' }}>🚀</span>
                    <h3 style={{
                      margin: '0 0 8px 0',
                      fontSize: isMobile ? '18px' : '20px',
                      fontWeight: '600',
                      color: '#e5e5e5'
                    }}>
                      Great discussions ahead!
                    </h3>
                    <p style={{
                      margin: '0 0 16px 0',
                      color: '#888',
                      fontSize: '14px'
                    }}>
                      Showing {threads.length} of {totalThreads} threads
                    </p>
                    <div style={{
                      width: '100%',
                      height: '4px',
                      background: '#333',
                      borderRadius: '2px',
                      overflow: 'hidden',
                      marginBottom: '16px'
                    }}>
                      <div style={{
                        width: `${(threads.length / totalThreads) * 100}%`,
                        height: '100%',
                        background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                        borderRadius: '2px',
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                    <button 
                      onClick={handleLoadMore}
                      disabled={loadingMore}
                      style={{
                        padding: isMobile ? '12px 24px' : '16px 32px',
                        background: loadingMore 
                          ? '#666' 
                          : 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '16px',
                        cursor: loadingMore ? 'not-allowed' : 'pointer',
                        fontSize: isMobile ? '14px' : '16px',
                        fontWeight: '600',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 4px 12px rgba(139, 92, 246, 0.4)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        margin: '0 auto'
                      }}
                    >
                      {loadingMore ? (
                        <>
                          <div className="spinner" style={{
                            width: '16px',
                            height: '16px',
                            border: '2px solid rgba(255,255,255,0.3)',
                            borderTop: '2px solid white',
                            borderRadius: '50%'
                          }} />
                          Loading...
                        </>
                      ) : (
                        <>
                          ✨ Load More Threads
                        </>
                      )}
                    </button>
                  </div>
                ) : (
                  <div>
                    <span style={{ fontSize: isMobile ? '28px' : '32px', marginBottom: '8px', display: 'block' }}>🎉</span>
                    <h3 style={{
                      margin: '0 0 8px 0',
                      fontSize: isMobile ? '18px' : '20px',
                      fontWeight: '600',
                      color: '#e5e5e5'
                    }}>
                      You've reached the end!
                    </h3>
                    <p style={{
                      margin: '0 0 16px 0',
                      color: '#888',
                      fontSize: '14px'
                    }}>
                      You've seen all {totalThreads} threads. Why not start a new discussion?
                    </p>
                    <button
                      onClick={() => setShowCreateThread(true)}
                      style={{
                        padding: '12px 24px',
                        background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        fontSize: isMobile ? '14px' : '16px',
                        fontWeight: '600'
                      }}
                    >
                      🌟 Start New Thread
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Sidebar - Hidden on mobile */}
          {!isMobile && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <CommunityStatsCard />
              <TrendingCard 
                isMobile={isMobile}
               />
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {createModalType === 'poll' ? (
        <EnhancedCreateThreadModal
          isOpen={showCreateThread}
          onClose={() => { setShowCreateThread(false); }}
          onCreateThread={handleCreateThread}
          userSeshRooms={userSeshRooms}
          initialThreadType="poll"
          key="poll"
        />
      ) : (
        <CreateThreadModal
          isOpen={showCreateThread}
          onClose={() => { setShowCreateThread(false); }}
          onCreateThread={handleCreateThread}
          userSeshRooms={userSeshRooms}
          key="discussion"
          colors={colors}
        />
      )}
      
      {showThreadDetail && selectedThread && (
        <ThreadDetailView 
          thread={selectedThread}
          onClose={() => {
            setShowThreadDetail(false);
            setSelectedThread(null);
          }}
          currentUserId={getCurrentUserId()}
        />
      )}
      
      {showProfileView && selectedUserId && (
        <PublicProfileView
          userId={selectedUserId}
          currentUserId={getCurrentUserId()}
          onClose={() => {
            setShowProfileView(false);
            setSelectedUserId(null);
          }}
        />
      )}
    </div>
  );
}